import React, { useState } from 'react';
import { Box, Button, Icon, Modal } from '@neshane/blueberry';
import { useDlq, useDlqActions } from '../hooks';
import { DlqStatusBadge } from '../components/StatusBadges';
import { OPERATION_TYPE_LABELS, PROVIDER_LABELS, DLQ_STATUS_LABELS } from '../labels';
import { PageEmptyState, Spinner } from '../../../common/components';

const STATUS_OPTIONS = Object.keys(DLQ_STATUS_LABELS);

function OperationsDlqContainer() {
    const [status, setStatus] = useState('');
    const [target, setTarget] = useState(null);
    const [decision, setDecision] = useState('resolved');
    const [notes, setNotes] = useState('');
    const { entries, total, loading, error, refresh } = useDlq({ status: status || undefined });
    const actions = useDlqActions();

    if (error) {
        return <p className="body-m text-secondary-alert-default">{error}</p>;
    }

    const closeModal = () => {
        setTarget(null);
        setDecision('resolved');
        setNotes('');
    };

    const submitResolve = async () => {
        if (!target) return;
        const body = {
            status: decision === 'ignored' ? 'IGNORED' : 'RESOLVED',
            decision,
            notes: notes || undefined,
        };
        await actions.resolve(target.id, body);
        closeModal();
    };

    return (
        <div className="flex flex-col gap-24">
            <div className="flex items-center justify-between">
                <div className="flex flex-col gap-4">
                    <h1 className="heading-l text-primary">صف مرگ (DLQ)</h1>
                    <p className="body-m text-secondary-bg-primary">عملیات ناموفق در انتظار بررسی دستی (تعداد کل: {total})</p>
                </div>
                <Button text="بروزرسانی" iconName="arrow-path" variant="secondaryNeutral" onClick={refresh} />
            </div>

            <Box className="flex flex-wrap items-end gap-16">
                <div className="flex flex-col gap-4">
                    <label className="body-s text-secondary-bg-primary">وضعیت</label>
                    <select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="rounded-12 border border-default p-8" title="لطفا این فیلد را پر کنید" required>
                        <option value="">همه</option>
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{DLQ_STATUS_LABELS[s]}</option>
                        ))}
                    </select>
                </div>
            </Box>

            {loading ? (
                <div className="flex flex-col items-center justify-center gap-16 py-64 text-secondary-bg-primary">
                    <Spinner />
                    <p className="body-m">در حال بارگذاری صف...</p>
                </div>
            ) : entries.length === 0 ? (
                <PageEmptyState
                    iconName="circle-check"
                    iconVariant="success"
                    title="صف مرگ خالی است"
                    description="عملیات ناموفقی در انتظار بررسی نیست"
                />
            ) : (
                <div className="flex flex-col gap-16">
                    {entries.map((entry) => (
                        <Box key={entry.id} className="flex flex-col gap-16">
                            <div className="flex items-center gap-16">
                                <span className="flex items-center justify-center rounded-12 bg-secondary-alert-default p-8">
                                    <Icon name="circle-strikeout" size="m" />
                                </span>
                                <div className="flex flex-1 flex-col gap-4">
                                    <h3 className="title-m text-primary">{OPERATION_TYPE_LABELS[entry.operationType] || entry.operationType}</h3>
                                    <p className="body-s text-secondary-bg-primary" dir="ltr">{entry.operationId}</p>
                                </div>
                                <DlqStatusBadge status={entry.status} />
                            </div>

                            <div className="flex flex-col gap-8">
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">سرویس:</span>
                                    <span className="body-m text-primary">{PROVIDER_LABELS[entry.provider] || entry.provider}</span>
                                </div>
                                <div className="flex items-center gap-8">
                                    <span className="body-s text-secondary-bg-primary">تعداد تلاش‌ها:</span>
                                    <span className="body-m text-primary">{entry.failureCount}</span>
                                </div>
                            </div>

                            {entry.lastErrorMessage && (
                                <div className="rounded-12 bg-secondary-alert-default p-12">
                                    <p className="body-s text-secondary-alert-default">{entry.lastErrorMessage}</p>
                                </div>
                            )}

                            <div className="flex justify-end gap-8">
                                <Button
                                    text="بازگشت به صف"
                                    variant="secondaryBrand"
                                    iconName="arrow-path"
                                    isDisabled={actions.busyId === entry.id}
                                    isLoading={actions.busyId === entry.id}
                                    onClick={() => actions.retry(entry.id)}
                                />
                                <Button
                                    text="بررسی و ثبت نتیجه"
                                    variant="secondaryNeutral"
                                    iconName="clipboard-pin"
                                    onClick={() => setTarget(entry)}
                                />
                            </div>
                        </Box>
                    ))}
                </div>
            )}

            {target && (
                <Modal
                    iconName="clipboard-pin"
                    iconVariant="purple"
                    title="ثبت نتیجه بررسی"
                    subtitle={OPERATION_TYPE_LABELS[target.operationType] || target.operationType}
                    isOpen
                    onClose={closeModal}
                    actions={
                        <div className="flex items-center gap-8">
                            <Button text="انصراف" variant="secondaryNeutral" onClick={closeModal} />
                            <Button
                                text="ثبت"
                                variant="primaryBrand"
                                isLoading={actions.busyId === target.id}
                                onClick={submitResolve}
                            />
                        </div>
                    }
                >
                    <div className="flex flex-col gap-16">
                        <p className="body-s text-secondary-bg-primary" dir="ltr">{target.operationId}</p>
                        <div className="flex flex-col gap-4">
                            <label className="body-s text-secondary-bg-primary">نتیجه</label>
                            <select
                                value={decision}
                                onChange={(e) => setDecision(e.target.value)}
                                className="rounded-12 border border-default p-8" title="لطفا این فیلد را پر کنید" required>
                                <option value="resolved">حل‌شده دستی</option>
                                <option value="ignored">نادیده گرفته شود</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-4">
                            <label className="body-s text-secondary-bg-primary">یادداشت</label>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                rows={3}
                                className="rounded-12 border border-default p-8"
                                placeholder="توضیحات بررسی..." title="توضیحات بررسی..." required />
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

export default OperationsDlqContainer;
